import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { and, eq, not } from 'tio-ui/utilities';
import { Section } from 'tio-ui/components/layout';
import { t } from 'ember-intl';
import { divide } from 'tio-ui/math';
import { tracked } from '@glimmer/tracking';
import Button from 'tio-reimagined/components/button';
import Component from '@glimmer/component';
import Popover from 'tio-reimagined/components/popover';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import {
  PhDotsThreeVertical,
  PhGraduationCap,
  PhPlus,
  PhNotePencil,
  PhTrash,
} from 'ember-phosphor-icons';
import type TasApplicationModel from 'tio-common/models/tas-application';
import type TasCourseModel from 'tio-common/models/tas-course';

interface StepExpenseItemsSignature {
  Args: {
    stepNumber: number;
    application: TasApplicationModel;
    expenseTypeField: string;
    onOpenEducationDrawer: (course: TasCourseModel | null) => void;
    onToggleDeleteCourse: (course: TasCourseModel | null) => void;
    onNext: () => void;
    hasAttempted: boolean;
    isComplete: boolean;
  };
}

export default class StepExpenseItems extends Component<StepExpenseItemsSignature> {
  @tracked openMenuCourseId: string | null = null;

  get displayNumber() {
    return this.args.stepNumber + 1;
  }

  get hasExpenseItems() {
    return (this.args.application.activeCourses?.length ?? 0) > 0;
  }

  get totalCents() {
    return (
      this.args.application.activeCourses?.reduce(
        (sum, course) => sum + (course.courseTotal ?? 0),
        0
      ) ?? 0
    );
  }

  toggleMenu = (courseId: string) => {
    this.openMenuCourseId = this.openMenuCourseId === courseId ? null : courseId;
  };

  closeMenu = () => {
    this.openMenuCourseId = null;
  };

  handleEdit = (course: TasCourseModel) => {
    this.openMenuCourseId = null;
    this.args.onOpenEducationDrawer(course);
  };

  handleDelete = (course: TasCourseModel) => {
    this.openMenuCourseId = null;
    this.args.onToggleDeleteCourse(course);
  };

  <template>
    <Section>
      <:header>
        <div class="flex items-center justify-between w-full">
          <div class="flex items-center gap-2">
            <span
              class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-violet-600 text-white text-xs font-bold"
            >{{this.displayNumber}}</span>
            <span>{{t "tas.guided.steps.add_expense_items"}}<span
                class="text-red-500 ml-0.5"
              >*</span></span>
          </div>
          {{#if this.hasExpenseItems}}
            <span class="font-semibold text-gray-900">
              {{t "tas.guided.steps.total"}}
              {{safeFormatNumber (divide this.totalCents 100) style="currency" currency="USD"}}
            </span>
          {{/if}}
        </div>
      </:header>
      <:body>
        <p class="text-gray-500 mb-8">{{t "tas.guided.steps.expense_items_description"}}</p>

        {{#if (and @hasAttempted (not this.hasExpenseItems))}}
          <AlertBanner @intent="warning" class="mb-4">
            <p class="text-sm font-medium">{{t "tas.guided.steps.expense_items_required"}}</p>
          </AlertBanner>
        {{/if}}

        {{#if this.hasExpenseItems}}
          {{! Expense item cards }}
          <div class="flex flex-col gap-3">
            {{#each @application.activeCourses as |course|}}
              <div
                class="bg-white border border-gray-200/70 rounded-xl p-4 flex items-center justify-between"
              >
                <div class="flex items-center gap-4">
                  <div
                    class="bg-gray-100 rounded-full w-6 h-6 flex items-center justify-center shrink-0"
                  >
                    <PhGraduationCap @size="18" class="text-gray-500" />
                  </div>
                  <span class="font-medium text-gray-800">{{course.courseName}}</span>
                </div>
                <div class="flex items-center gap-4">
                  <span class="font-semibold text-gray-900">
                    {{#if @application.isCreditBased}}
                      {{course.courseCredit}}
                      {{t "common.credits"}}
                    {{else}}
                      {{safeFormatNumber
                        (divide course.courseTotal 100)
                        style="currency"
                        currency="USD"
                      }}
                    {{/if}}
                  </span>
                  <Popover
                    @placement="bottom-end"
                    @isOpen={{eq this.openMenuCourseId course.id}}
                    @onClose={{this.closeMenu}}
                    @maxWidth={{180}}
                  >
                    <:trigger as |pop|>
                      <button
                        type="button"
                        data-test-edit-education-drawer
                        class="text-gray-400 hover:text-gray-600"
                        {{pop.register}}
                        {{on "click" (fn this.toggleMenu course.id)}}
                      >
                        <PhDotsThreeVertical @size="20" />
                      </button>
                    </:trigger>
                    <:body>
                      <div class="flex flex-col py-1">
                        <button
                          type="button"
                          class="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                          {{on "click" (fn this.handleEdit course)}}
                        >
                          <PhNotePencil @size="16" />
                          {{t "common.edit"}}
                        </button>
                        <button
                          type="button"
                          class="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded"
                          {{on "click" (fn this.handleDelete course)}}
                        >
                          <PhTrash @size="16" />
                          {{t "common.delete"}}
                        </button>
                      </div>
                    </:body>
                  </Popover>
                </div>
              </div>
            {{/each}}

            {{! + Add Item row }}
            <button
              type="button"
              class="bg-white border border-gray-200/70 rounded-xl p-4 flex items-center justify-center gap-2 text-link font-semibold hover:bg-gray-50 transition-colors"
              data-test-add-education-drawer
              {{on "click" (fn @onOpenEducationDrawer null)}}
            >
              <PhPlus @size="18" />
              {{t "tas.guided.steps.add_item"}}
            </button>
          </div>
        {{else}}
          {{! Empty state }}
          <div
            class="border border-gray-200/70 rounded-2xl py-8 flex flex-col items-center justify-center gap-2"
          >
            <PhGraduationCap @size="32" class="text-gray-500" />
            <div class="flex flex-col items-center gap-1 pb-2">
              <span class="font-semibold text-lg text-gray-900">{{t
                  "tas.guided.steps.no_expense_items"
                }}</span>
              <span class="font-medium text-gray-500 text-center max-w-86">{{t
                  "tas.guided.steps.no_expense_items_description"
                }}</span>
            </div>
            <Button
              @intent="primary"
              data-test-add-education-drawer
              {{on "click" (fn @onOpenEducationDrawer null)}}
            >
              {{t "tas.guided.steps.add_expense"}}
            </Button>
          </div>
        {{/if}}

        {{! Next button }}
        <div class="flex pt-8">
          <Button @intent="primary" data-test-next-step {{on "click" @onNext}}>
            {{t "common.next"}}
          </Button>
        </div>
      </:body>
    </Section>
  </template>
}
